/**
 * Shared Pause Button Component
 * 
 * Wiederverwendbare Pause-Button Komponente für Carousels/Sliders
 * WCAG 2.2.2 Level A Konformität
 */

// SVG Icons
const ICON_PAUSE = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
const ICON_PLAY = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="7,4 19,12 7,20" stroke="currentColor" stroke-width="2" stroke-linejoin="round" /></svg>';

export interface PauseButtonConfig {
    /** CSS-Klasse für den Button */
    className: string;
    /** Callback wenn Play geklickt wird */
    onPlay: () => void;
    /** Callback wenn Pause geklickt wird */
    onPause: () => void;
    /** Optionale Callback für Position-Updates */
    onPositionUpdate?: (button: HTMLButtonElement) => void;
    /** Debug-Logging aktivieren */
    debug?: boolean;
}

export interface PauseButtonInstance {
    /** Das Button-Element */
    element: HTMLButtonElement;
    /** Pause-Status abfragen */
    isPaused: () => boolean;
    /** Programmatisch pausieren */
    pause: () => void;
    /** Programmatisch fortsetzen */
    play: () => void;
    /** Button entfernen */
    destroy: () => void;
}

/**
 * Erstellt einen Pause-Button für Carousels/Sliders
 */
export function createPauseButton(config: PauseButtonConfig): PauseButtonInstance {
    const { className, onPlay, onPause, onPositionUpdate, debug = false } = config;
    
    const pauseButton = document.createElement('button');
    pauseButton.className = className;
    pauseButton.setAttribute('aria-label', 'Automatische Wiedergabe pausieren');
    pauseButton.setAttribute('type', 'button');

    pauseButton.innerHTML = ICON_PAUSE;

    let isPaused = false;

    function log(...args: unknown[]): void {
        if (debug) {
            console.log('PauseButton:', ...args);
        }
    }

    function updateButtonState(paused: boolean): void {
        pauseButton.setAttribute('aria-label', paused ? 'Automatische Wiedergabe fortsetzen' : 'Automatische Wiedergabe pausieren');
        pauseButton.innerHTML = paused ? ICON_PLAY : ICON_PAUSE;
    }

    function handlePause(): void {
        isPaused = true;
        updateButtonState(true);
        onPause();
        log('Pausiert');
    }

    function handlePlay(): void {
        isPaused = false;
        updateButtonState(false);
        onPlay();
        log('Fortgesetzt');
    }

    pauseButton.addEventListener('click', () => {
        if (isPaused) {
            handlePlay();
        } else {
            handlePause();
        }
    });

    // Optional: Position-Updates
    if (onPositionUpdate) {
        onPositionUpdate(pauseButton);
    }

    return {
        element: pauseButton,
        isPaused: () => isPaused,
        pause: handlePause,
        play: handlePlay,
        destroy: () => {
            pauseButton.remove();
            log('Button entfernt');
        }
    };
}
